import {
	Paper,
	SortDirection,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { visuallyHidden } from "@mui/utils"
import React, { MouseEvent } from "react"
import { useSelector } from "react-redux"

import { RootState } from "../../../store"
import { CorporatesState } from "../../../store/actions/bank"
import { CorporatesListState } from "../../../store/actions/corporates"
import NoData from "../../error/nodata"
import UserRow from "./row"

interface IEnhancedCorporateTable {
	sortCriteria: [{ field: string; order: string }]
	setSortCriteria: (value: { field: string; order: string }[]) => void
}

interface IEnhancedTableHeadProps {
	sortCriteria: [{ field: string; order: string }]
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof CorporatesState
	) => void
}

interface HeadCell {
	id: keyof CorporatesState
	label: string
	numeric: boolean
	disablePadding: boolean
}

const TableCellStyled = styled(TableCell)(() => ({
	paddingLeft: "8px",
}))

const TableHeadStyled = styled(TableHead)(() => ({
	backgroundColor: "#E9EDF0",
}))

const lang = window.localStorage.getItem("i18nextLng")
const headCells: HeadCell[] = [
	{
		id: "corporateName",
		numeric: false,
		disablePadding: false,
		label: lang == "it" ? "Nome" : "Name",
	},
	{
		id: "contiCorrenti",
		numeric: true,
		disablePadding: false,
		label: lang == "it" ? "Conti correnti" : "Account",
	},
	{
		id: "enabled",
		numeric: false,
		disablePadding: false,
		label: lang == "it" ? "Abilitato" : "Enabled",
	},
]

function EnhancedTableHead(props: IEnhancedTableHeadProps) {
	const { sortCriteria, onRequestSort } = props
	const createSortHandler =
		(property: keyof CorporatesState) => (event: MouseEvent<unknown>) => {
			onRequestSort(event, property)
		}

	return (
		<TableHeadStyled>
			<TableRow>
				{headCells.map((headCell) => {
					const cellSorted:
						| { field: string; order: SortDirection }
						| undefined = sortCriteria.find(
						(criteria: { field: string; order: SortDirection }) =>
							criteria.field === headCell.id
					)
					return (
						<TableCellStyled
							key={headCell.id}
							align={headCell.numeric ? "right" : "left"}
							padding={headCell.disablePadding ? "none" : "normal"}
							sortDirection={!!cellSorted ? cellSorted.order : undefined}
						>
							<TableSortLabel
								active={!!cellSorted}
								direction={!!cellSorted ? cellSorted.order : "asc"}
								onClick={createSortHandler(headCell.id)}
							>
								{headCell.label}{" "}
								{!!cellSorted ? (
									<span style={visuallyHidden}>
										{" "}
										{cellSorted.order === "desc"
											? "sorted descending"
											: "sorted ascending"}{" "}
									</span>
								) : null}
							</TableSortLabel>
						</TableCellStyled>
					)
				})}
				<TableCellStyled />
			</TableRow>
		</TableHeadStyled>
	)
}

const CorporateEnhancedTable = ({
	sortCriteria,
	setSortCriteria,
}: IEnhancedCorporateTable) => {
	const corporatesState: CorporatesListState = useSelector(
		(state: RootState) => state.corporatesPaginated
	)

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof CorporatesState
	) => {
		const cellSorted: { field: string; order: SortDirection } | undefined =
			sortCriteria.find(
				(criteria: { field: string; order: SortDirection }) =>
					criteria.field === property
			)
		const isAsc = !!cellSorted ? cellSorted.order : false
		setSortCriteria([{ field: property, order: isAsc ? "desc" : "asc" }])
	}

	return (
		<TableContainer component={Paper} sx={{ marginTop: "16px" }}>
			<Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
				<EnhancedTableHead
					sortCriteria={sortCriteria}
					onRequestSort={handleRequestSort}
				/>
				<TableBody>
					{corporatesState?.list?.page?.content &&
						corporatesState.list.page.content.map((row: CorporatesState) => (
							<UserRow row={row} />
						))}

					{corporatesState?.list?.page?.content?.length === 0 && (
						<TableRow>
							<TableCellStyled
								colSpan={9}
								style={{ textAlign: "center" }}
								component="td"
								scope="row"
								padding="none"
							>
								<NoData />
							</TableCellStyled>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	)
}
export default CorporateEnhancedTable
